import { getUserRoles } from "@/lib/storeManager";
import { UserRole } from "@/services/APIs/UserRoleService";
import PTSNavigationRoutes from "./navigationRoutes";

export enum UserRoleType {
  EMPLOYEE = "PTS_Employee",
  IT = "PTS_IT",
  ADMINISTRATIVE = "Administrative",
  SUPER_ADMIN = "PTS_Super_Admin",
  USER = "user",
}

/**
 * Checks if user has a specific role (exact match)
 */
export const hasRole = (roleName: string): boolean => {
  const roles = getUserRoles();
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  return roles.some((role: UserRole) => role.role_name === roleName);
};

/**
 * Checks if user has a role that matches a pattern (case-insensitive)
 */
export const hasRolePattern = (pattern: string): boolean => {
  const roles = getUserRoles();
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  const lowerPattern = pattern.toLowerCase();
  return roles.some((role: UserRole) =>
    role.role_name.toLowerCase().includes(lowerPattern)
  );
};

/**
 * Checks if user has administrative access (full access)
 */
export const isAdministrative = (): boolean => {
  const roles = getUserRoles();
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  // Check for exact matches or patterns that indicate admin access
  return roles.some((role: UserRole) => {
    const roleName = role.role_name.toLowerCase();
    return (
      roleName === "administrative" ||
      roleName === "user" ||
      roleName === "pts_super_admin" ||
      roleName.includes("admin") ||
      roleName.includes("administrative")
    );
  });
};

/**
 * Checks if user has employee access (limited access)
 */
export const isEmployee = (): boolean => {
  const roles = getUserRoles();
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  // Check for employee role (case-insensitive)
  return roles.some((role: UserRole) => {
    const roleName = role.role_name.toLowerCase();
    return (
      roleName === "PTS_Employee" ||
      roleName.includes("employee") ||
      roleName.includes("PTS_Employee")
    );
  });
};

export const isUser = (): boolean => {
  const roles = getUserRoles();
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  return roles.some((role: UserRole) => role.role_name === "user");
};

/**
 * Checks if user has HR role (PTS_HR)
 */
export const isHR = (): boolean => {
  const roles = getUserRoles();
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  // Check for HR role (case-insensitive)
  return roles.some((role: UserRole) => {
    const roleName = role.role_name.toLowerCase();
    return (
      roleName === "PTS_HR" ||
      roleName === "pts_hr" ||
      roleName.includes("pts_hr") ||
      roleName.includes("PTS_HR") ||
      roleName === "hr"
    );
  });
};

/**
 * Checks if user is Super Admin
 */
export const isSuperAdmin = (): boolean => {
  const roles = getUserRoles();
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  return roles.some((role: UserRole) => {
    const roleName = role.role_name.toLowerCase();
    return roleName === "pts_super_admin";
  });
};

/**
 * Checks if user has PTS_PM role (project management sidebar + routes)
 */
export const isPTS_PM = (): boolean => {
  const roles = getUserRoles();
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  return roles.some((role: UserRole) => {
    const roleName = role.role_name.toLowerCase();
    return roleName === "pts_pm";
  });
};

/**
 * Checks if user has PTS_BA role (same as PM except Spent Hours)
 */
export const isPTS_BA = (): boolean => {
  const roles = getUserRoles();
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  return roles.some((role: UserRole) => {
    const roleName = role.role_name.toLowerCase();
    return roleName === "pts_ba";
  });
};

/**
 * Checks if user has PTS_Accounting role (broad module access; not PM/BA menu lists)
 */
export const isRestrictedRole = (): boolean => {
  const roles = getUserRoles();
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  return roles.some((role: UserRole) => {
    const roleName = role.role_name.toLowerCase();
    return roleName === "pts_accounting";
  });
};

/**
 * Checks if user has PTS_Account role (sidebar: employee dashboard, employee, project, spent hours, attendance, project summary, billing summary, daily work item report, leave application, holiday list)
 */
export const isPTSAccount = (): boolean => {
  const roles = getUserRoles();
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  return roles.some((role: UserRole) => {
    const roleName = role.role_name.toLowerCase();
    return roleName === "pts_account";
  });
};

/**
 * Checks if user has the PTS_IT role.
 * PTS_IT inherits employee routes and can also access Expense Summary.
 */
export const isPTSIT = (): boolean => {
  const roles = getUserRoles();
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  return roles.some(
    (role: UserRole) => role.role_name.toLowerCase() === "pts_it"
  );
};

/**
 * Checks if user has PTS_Sprint_Manager role.
 * This role can only access sprint-related modules plus employee dashboard.
 */
export const isPTSSprintManager = (): boolean => {
  const roles = getUserRoles();
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  return roles.some((role: UserRole) => {
    const roleName = role.role_name.toLowerCase();
    return roleName === "pts_sprint_manager";
  });
};

/**
 * Checks if user can access the self-service My Attendance Report page.
 * Available to all authenticated PTS users (any role).
 */
export const canAccessMyAttendanceReport = (): boolean => {
  const roles = getUserRoles();
  return !!roles && Array.isArray(roles) && roles.length > 0;
};

/**
 * Checks if user has PTS_Admin role
 */
export const isPTSAdmin = (): boolean => {
  return (
    isSuperAdmin() || hasRole("PTS_Admin") || hasRole("pts_admin") || isUser()
  );
};

/** Expense Summary is accessible to PTS_Admin, PTS_IT, and PTS_Account. */
export const canAccessExpenseSummary = (): boolean => {
  return isPTSAdmin() || isPTSIT() || isPTSAccount();
};

/**
 * Billing Invoice: PTS_Account and PTS_Admin only
 */
export const canAccessBillingInvoice = (): boolean => {
  return isPTSAccount() || isPTSAdmin();
};

/**
 * Change Requests module: PTS_BA, PTS_PM, PTS_Account, PTS_Admin
 */
export const canAccessChangeRequests = (): boolean => {
  return isPTSAdmin() || isPTS_BA() || isPTS_PM() || isPTSAccount() || isHR();
};

export type ChangeRequestVisibleTab = "ba" | "management" | "accountant";

/**
 * Role-based Change Request tabs:
 * - PTS_BA / PTS_PM → Business Analyst only
 * - PTS_Account → Accountant only
 * - PTS_Admin (and Super Admin / HR) → all tabs
 */
export const getChangeRequestVisibleTabs = (): ChangeRequestVisibleTab[] => {
  if (isPTSAdmin() || isHR()) {
    return ["ba", "management", "accountant"];
  }

  // Account-only users (not also admin)
  if (isPTSAccount() && !isPTS_BA() && !isPTS_PM()) {
    return ["accountant"];
  }

  // BA / PM → first tab only
  if (isPTS_BA() || isPTS_PM()) {
    return ["ba"];
  }

  // Fallback for other roles that can open the route
  if (canAccessChangeRequests()) {
    return ["ba", "management", "accountant"];
  }

  return [];
};

export const canAccessChangeRequestTab = (
  tab: ChangeRequestVisibleTab
): boolean => {
  return getChangeRequestVisibleTabs().includes(tab);
};

/**
 * Purchase Approvals is available to every authenticated PTS role.
 */
export const canAccessPurchaseApprovals = (): boolean => {
  return canAccessMyAttendanceReport();
};

export type PurchaseApprovalVisibleTab = "ba" | "management" | "accountant";

/**
 * Role-based Purchase Approval tabs:
 * - All authenticated roles → Employee
 * - PTS_Account / PTS_IT → Employee and Accountant
 * - PTS_Admin → all tabs
 */
export const getPurchaseApprovalVisibleTabs =
  (): PurchaseApprovalVisibleTab[] => {
    if (isPTSAdmin()) {
      return ["ba", "management", "accountant"];
    }

    if (isPTSAccount() || isPTSIT()) {
      return ["ba", "accountant"];
    }

    if (canAccessPurchaseApprovals()) {
      return ["ba"];
    }

    return [];
  };

export const canAccessPurchaseApprovalTab = (
  tab: PurchaseApprovalVisibleTab
): boolean => {
  return getPurchaseApprovalVisibleTabs().includes(tab);
};

/**
 * Leave Requests page is available to every authenticated PTS role.
 */
export const canAccessLeaveRequests = (): boolean => {
  return canAccessMyAttendanceReport();
};

export type LeaveRequestVisibleTab = "employee" | "management";

/**
 * Management tab: PTS_Admin and PTS_HR only.
 */
export const canAccessLeaveRequestManagement = (): boolean => {
  return isPTSAdmin() || isHR();
};

/**
 * Role-based Leave Request tabs:
 * - All authenticated roles → Employee
 * - PTS_Admin / PTS_HR → Employee and Management
 */
export const getLeaveRequestVisibleTabs = (): LeaveRequestVisibleTab[] => {
  if (canAccessLeaveRequestManagement()) {
    return ["employee", "management"];
  }

  if (canAccessLeaveRequests()) {
    return ["employee"];
  }

  return [];
};

export const canAccessLeaveRequestTab = (
  tab: LeaveRequestVisibleTab
): boolean => {
  return getLeaveRequestVisibleTabs().includes(tab);
};

/**
 * Project Setting: PTS_Account, PTS_Admin, and PTS_PM
 */
export const canAccessProjectSetting = (): boolean => {
  return isPTSAccount() || isPTSAdmin() || isPTS_PM();
};

/**
 * Billing sidebar section: visible when user can access any billing submenu
 */
export const canAccessBillingModule = (): boolean => {
  return canAccessBillingInvoice() || canAccessProjectSetting();
};

/**
 * Checks if user can access Attendance and Employee Check-in pages.
 * Same right for HR (PTS_HR) and PTS_Admin (and Super Admin).
 */
export const canAccessAttendanceAndCheckin = (): boolean => {
  return (
    isSuperAdmin() ||
    isHR() ||
    hasRole("PTS_Admin") ||
    hasRole("pts_admin") ||
    isUser()
  );
};

/**
 * Checks if user can access the Remaining Time Report page.
 * Hidden from PTS_Employee, PTS_Account, and PTS_BA roles.
 */
export const canAccessRemainingTimeReport = (): boolean => {
  if (isEmployee() || isPTSAccount() || isPTS_BA()) {
    return false;
  }

  return (
    isSuperAdmin() ||
    isPTSAdmin() ||
    isHR() ||
    isPTS_PM() ||
    isRestrictedRole() ||
    isAdministrative()
  );
};

/**
 * Checks if user can access a specific route based on their role
 */
export const canAccessRoute = (route: string): boolean => {
  // Public routes that anyone can access
  const publicRoutes = [
    PTSNavigationRoutes.ADMIN_LOGIN_PAGE,
    "/forgot-password-reset",
  ];

  if (publicRoutes.some(p => route.includes(p))) {
    return true;
  }

  // Notifications page is available to all authenticated PTS users.
  if (route.includes(PTSNavigationRoutes.NOTIFICATIONS)) {
    return true;
  }

  // Chat page is available to all authenticated PTS users.
  if (route.includes(PTSNavigationRoutes.CHAT)) {
    return true;
  }

  // My Attendance Report: self-service page for all authenticated PTS users
  if (route.includes(PTSNavigationRoutes.MY_ATTENDANCE_REPORT)) {
    return canAccessMyAttendanceReport();
  }

  // Service Desk Tasks: available to all authenticated PTS users
  if (route.includes(PTSNavigationRoutes.SERVICE_DESK_TASKS)) {
    return canAccessMyAttendanceReport();
  }

  // Expense Summary + Dashboard: PTS_Admin and PTS_IT only.
  if (
    route.includes(PTSNavigationRoutes.EXPENSE_SUMMARY) ||
    route.includes(PTSNavigationRoutes.EXPENSE_DASHBOARD)
  ) {
    return canAccessExpenseSummary();
  }

  // Remaining Time Report: not available to Employee, PTS_Account, or PTS_BA
  if (route.includes(PTSNavigationRoutes.REMAINING_TIME_REPORT)) {
    return canAccessRemainingTimeReport();
  }

  // Change Requests: PTS_BA, PTS_PM, PTS_Account, PTS_Admin
  if (route.includes(PTSNavigationRoutes.CHANGE_REQUESTS)) {
    return canAccessChangeRequests();
  }

  // Purchase Approvals: available to all authenticated PTS roles
  if (route.includes(PTSNavigationRoutes.PURCHASE_APPROVALS)) {
    return canAccessPurchaseApprovals();
  }

  // Leave Requests: all authenticated PTS roles can open the page (Employee tab).
  // Management tab is restricted to PTS_Admin and PTS_HR.
  if (route.includes(PTSNavigationRoutes.LEAVE_REQUESTS)) {
    if (route.includes("tab=management")) {
      return canAccessLeaveRequestManagement();
    }
    return canAccessLeaveRequests();
  }

  // Billing Invoice: PTS_Account and PTS_Admin only
  if (route.includes(PTSNavigationRoutes.BILLING_INVOICE)) {
    return canAccessBillingInvoice();
  }

  // Project Setting: PTS_Account, PTS_Admin, and PTS_PM
  if (route.includes(PTSNavigationRoutes.BILLING_PROJECT_SETTING)) {
    return canAccessProjectSetting();
  }

  // Attendance: allow HR, PTS_Admin, PTS_Account, and PTS_PM
  if (route.includes(PTSNavigationRoutes.ATTENDANCE)) {
    return canAccessAttendanceAndCheckin() || isPTSAccount() || isPTS_PM();
  }
  // Employee Check-in: same right for HR and PTS_Admin only (not PTS_Account)
  if (route.includes(PTSNavigationRoutes.EMPLOYEE_CHECKIN)) {
    return canAccessAttendanceAndCheckin();
  }

  const roles = getUserRoles();
  if (!roles || !Array.isArray(roles)) {
    return false;
  }

  // Super Admin or PTS_Admin: Show all (PTS_Admin is handled by isUser in some places, but let's be explicit)
  const userIsAdmin = roles.some((role: UserRole) => {
    const roleName = role.role_name.toLowerCase();
    return (
      roleName === "pts_super_admin" ||
      roleName === "pts_admin" ||
      roleName === "user"
    );
  });

  if (userIsAdmin) {
    return true;
  }

  // HR role permissions
  if (isHR()) {
    // HR can access everything except Roles List and Role Permission Manager
    const restrictedPaths = [
      PTSNavigationRoutes.ROLES_LIST,
      PTSNavigationRoutes.ROLE_PERMISSION_MANAGER,
    ];
    return !restrictedPaths.some(p => route.includes(p));
  }

  // PTS_Account: only specific routes (employee dashboard, employee, project, spent hours, attendance, project summary, billing summary, daily spent hours, leave application, holiday list)
  if (isPTSAccount()) {
    const allowedPathPrefixes = [
      PTSNavigationRoutes.EMPLOYEE_DASHBOARD,
      PTSNavigationRoutes.EMPLOYEE_PROFILE,
      PTSNavigationRoutes.EMPLOYEE,
      PTSNavigationRoutes.PROJECTS,
      PTSNavigationRoutes.CHANGE_REQUESTS,
      PTSNavigationRoutes.PURCHASE_APPROVALS,
      PTSNavigationRoutes.LEAVE_REQUESTS,
      "/project", // project create/edit/import use /project/*
      PTSNavigationRoutes.SPENT_HOURS,
      PTSNavigationRoutes.ATTENDANCE,
      PTSNavigationRoutes.MY_ATTENDANCE_REPORT,
      PTSNavigationRoutes.PROJECT_SUMMARY,
      PTSNavigationRoutes.BILLING_SUMMARY,
      PTSNavigationRoutes.EXPENSE_SUMMARY,
      PTSNavigationRoutes.EXPENSE_DASHBOARD,
      PTSNavigationRoutes.DAILY_SPENT_HOURS,
      PTSNavigationRoutes.LEAVE_APPLICATION,
      PTSNavigationRoutes.HOLIDAY_LIST,
      PTSNavigationRoutes.DESIGNATION_MASTER,
      PTSNavigationRoutes.BILLING_INVOICE,
      PTSNavigationRoutes.BILLING_PROJECT_SETTING,
    ];
    return allowedPathPrefixes.some(
      allowedRoute =>
        route === allowedRoute || route.startsWith(allowedRoute + "/")
    );
  }

  // PTS_Sprint_Manager: only employee dashboard and sprint-related modules
  if (isPTSSprintManager()) {
    const allowedPathPrefixes = [
      PTSNavigationRoutes.EMPLOYEE_DASHBOARD,
      PTSNavigationRoutes.EMPLOYEE_PROFILE,
      PTSNavigationRoutes.SPRINT,
      PTSNavigationRoutes.SPRINT_TASK_ALLOCATION,
      PTSNavigationRoutes.TASKS,
      PTSNavigationRoutes.MY_ATTENDANCE_REPORT,
      PTSNavigationRoutes.PURCHASE_APPROVALS,
      PTSNavigationRoutes.LEAVE_REQUESTS,
    ];
    return allowedPathPrefixes.some(
      allowedRoute =>
        route === allowedRoute || route.startsWith(allowedRoute + "/")
    );
  }

  const ptsPMAllowedPathPrefixes = [
    PTSNavigationRoutes.EMPLOYEE_DASHBOARD,
    PTSNavigationRoutes.EMPLOYEE_PROFILE,
    PTSNavigationRoutes.PROJECTS,
    PTSNavigationRoutes.CHANGE_REQUESTS,
    PTSNavigationRoutes.PURCHASE_APPROVALS,
    PTSNavigationRoutes.LEAVE_REQUESTS,
    "/project",
    PTSNavigationRoutes.PROJECT_TEMPLATES,
    PTSNavigationRoutes.SPRINT,
    PTSNavigationRoutes.SPRINT_TASK_ALLOCATION,
    PTSNavigationRoutes.TASKS,
    PTSNavigationRoutes.SPENT_HOURS,
    PTSNavigationRoutes.ATTENDANCE,
    PTSNavigationRoutes.MY_ATTENDANCE_REPORT,
    PTSNavigationRoutes.PROJECT_SUMMARY,
    PTSNavigationRoutes.RESOURCE_ALLOCATION,
    PTSNavigationRoutes.BILLING_PROJECT_SETTING,
  ];

  if (isPTS_PM()) {
    return ptsPMAllowedPathPrefixes.some(
      allowedRoute =>
        route === allowedRoute || route.startsWith(allowedRoute + "/")
    );
  }

  if (isPTS_BA()) {
    const baPrefixes = ptsPMAllowedPathPrefixes.filter(
      p => p !== PTSNavigationRoutes.SPENT_HOURS
    );
    return baPrefixes.some(
      allowedRoute =>
        route === allowedRoute || route.startsWith(allowedRoute + "/")
    );
  }

  // PTS_Accounting: broad access except roles, role permission, leave; no holiday list
  if (isRestrictedRole()) {
    // Restricted roles can access everything except Roles List, Role Permission, Leave Application
    const restrictedPaths = [
      PTSNavigationRoutes.ROLES_LIST,
      PTSNavigationRoutes.ROLE_PERMISSION_MANAGER,
      PTSNavigationRoutes.LEAVE_APPLICATION,
    ];
    // Special check for Holiday List: only visible to HR and Admin
    if (route.includes(PTSNavigationRoutes.HOLIDAY_LIST)) {
      return false;
    }
    return !restrictedPaths.some(p => route.includes(p));
  }

  // Administrative users (non-super-admin but with admin in name)
  if (isAdministrative()) {
    // Administrative can access everything except Roles List and Role Permission Manager
    const restrictedPaths = [
      PTSNavigationRoutes.ROLES_LIST,
      PTSNavigationRoutes.ROLE_PERMISSION_MANAGER,
    ];
    return !restrictedPaths.some(p => route.includes(p));
  }

  // Employee role restrictions
  if (isEmployee()) {
    // Employees can only access:
    // - Employee Dashboard
    // - Employee Profile (their own)
    // - Sprint Task Allocation (their own employee data only)
    // - My Attendance Report (self-service)
    const allowedRoutes = [
      PTSNavigationRoutes.EMPLOYEE_DASHBOARD,
      PTSNavigationRoutes.EMPLOYEE_PROFILE,
      PTSNavigationRoutes.SPRINT_TASK_ALLOCATION,
      PTSNavigationRoutes.MY_ATTENDANCE_REPORT,
      PTSNavigationRoutes.PURCHASE_APPROVALS,
      PTSNavigationRoutes.LEAVE_REQUESTS,
    ];

    return allowedRoutes.some(
      allowedRoute =>
        route.includes(allowedRoute) || route.endsWith(allowedRoute)
    );
  }

  // PTS_IT inherits all employee routes and adds Expense Summary + Dashboard.
  if (isPTSIT()) {
    const allowedRoutes = [
      PTSNavigationRoutes.EMPLOYEE_DASHBOARD,
      PTSNavigationRoutes.EMPLOYEE_PROFILE,
      PTSNavigationRoutes.SPRINT_TASK_ALLOCATION,
      PTSNavigationRoutes.MY_ATTENDANCE_REPORT,
      PTSNavigationRoutes.PURCHASE_APPROVALS,
      PTSNavigationRoutes.LEAVE_REQUESTS,
      PTSNavigationRoutes.EXPENSE_SUMMARY,
      PTSNavigationRoutes.EXPENSE_DASHBOARD,
    ];

    return allowedRoutes.some(
      allowedRoute =>
        route.includes(allowedRoute) || route.endsWith(allowedRoute)
    );
  }

  // Default: no access
  return false;
};

export const isReadOnlyRoute = (route: string): boolean => {
  // Administrative users have full access (not read-only)
  if (isAdministrative()) {
    return false;
  }

  // Employees have read-only access to attendance routes
  if (isEmployee()) {
    return route.includes("/attendance");
  }

  return false;
};
